import { useEffect, useState } from 'react'
import { HashRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from './context/AuthContext'
import { AuthPage } from './components/AuthPage'
import { useSocket } from './hooks/useSocket'
import { AppLayout } from './layouts/AppLayout'
import { DashboardPage } from './pages/DashboardPage'
import { AccountsPage } from './pages/AccountsPage'
import { JobsPage } from './pages/JobsPage'
import { ContactsPage } from './pages/ContactsPage'
import { OnboardingPage } from './pages/OnboardingPage'
import api from './lib/api'

function isOnboardingDone(userId) {
  if (!userId) return false
  try { return localStorage.getItem(`onboarding_done_${userId}`) === '1' } catch { return false }
}

function OnboardingGate({ user }) {
  const navigate = useNavigate()
  const location = useLocation()
  const [checked, setChecked] = useState(false)

  useEffect(() => {
    let cancelled = false
    const check = async () => {
      if (!user) { setChecked(true); return }
      if (isOnboardingDone(user.id)) { setChecked(true); return }
      try {
        const res = await api.get('/imap-accounts')
        if (cancelled) return
        if ((res.data?.length || 0) === 0 && location.pathname !== '/onboarding') {
          navigate('/onboarding', { replace: true })
        }
      } catch { /* network — skip redirect */ }
      finally { if (!cancelled) setChecked(true) }
    }
    check()
    return () => { cancelled = true }
  }, [user, navigate, location.pathname])

  return null
}

function AuthenticatedApp() {
  const { user, session } = useAuth()
  const { socket } = useSocket(session)

  return (
    <HashRouter>
      <OnboardingGate user={user} />
      <Routes>
        <Route path="/onboarding" element={<AppLayout socket={socket} />}>
          <Route index element={<OnboardingPage />} />
        </Route>
        <Route path="/" element={<AppLayout socket={socket} />}>
          <Route index element={<DashboardPage />} />
          <Route path="accounts" element={<AccountsPage />} />
          <Route path="jobs" element={<JobsPage />} />
          <Route path="contacts" element={<ContactsPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </HashRouter>
  )
}

function AppContent() {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="auth-page">
        <div className="auth-loading">Loading…</div>
      </div>
    )
  }

  if (!user) return <AuthPage />

  return <AuthenticatedApp />
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  )
}

export default App
